/**
 * Per-feature classification cache for the fuel-load tiles (issue #276, 2b).
 *
 * `fuelCharacteristics` / `strataBreakdown` arrive as JSON strings on every
 * vector-tile feature, and every camera settle used to re-JSON.parse and
 * re-classify all of them. Features are stable per tiles URL and carry a
 * promoted `__id`, so the parsed spec/ground result is cached per source and
 * invalidated when the tiles URL changes (a strategy recompute). Shared by the
 * tree layer and MapDimFilm so the work happens once.
 */

import type { Map as MapboxMap } from "mapbox-gl";
import {
  classifyGroundCover,
  classifyStand,
  parseCharacteristics,
  type GroundCoverSpec,
  type GroundKind,
  type StandSpec,
} from "../fuelClassification";

export interface FuelFeatureInfo {
  spec: StandSpec | null;
  ground: GroundCoverSpec | null;
}

/** Entries per source before the cache resets (overzoom keeps this bounded). */
const MAX_ENTRIES = 200000;

interface SourceCache {
  version: string;
  entries: Map<string | number, FuelFeatureInfo>;
}

const caches = new Map<string, SourceCache>();

/** The source's current tiles URL — the cache's invalidation key. */
function sourceVersion(map: MapboxMap, sourceId: string): string {
  try {
    const src = map.getSource(sourceId) as { tiles?: string[] } | undefined;
    return src?.tiles?.join("|") ?? "";
  } catch {
    return "";
  }
}

function classifyFeature(feature: GeoJSON.Feature): FuelFeatureInfo {
  const props = feature.properties as Record<string, unknown> | null;
  const chars = parseCharacteristics(props?.fuelCharacteristics);
  const spec = classifyStand(chars);
  const strata = parseCharacteristics(props?.strataBreakdown);
  const fuelLoadTop = Number(props?.fuelLoad);
  const kind: GroundKind = spec ? spec.kind : "grass";
  const ground = classifyGroundCover(
    chars,
    strata,
    kind,
    Number.isFinite(fuelLoadTop) ? fuelLoadTop : null
  );
  return { spec, ground };
}

/**
 * Classify a fuel feature through the per-source cache. Features without a
 * promoted id are classified directly (no caching).
 */
export function getFuelFeatureInfo(
  map: MapboxMap,
  sourceId: string,
  feature: GeoJSON.Feature
): FuelFeatureInfo {
  const id = feature.id;
  if (id === undefined || id === null) return classifyFeature(feature);
  const version = sourceVersion(map, sourceId);
  let cache = caches.get(sourceId);
  if (!cache || cache.version !== version) {
    cache = { version, entries: new Map() };
    caches.set(sourceId, cache);
  }
  const hit = cache.entries.get(id as string | number);
  if (hit) return hit;
  const info = classifyFeature(feature);
  if (cache.entries.size >= MAX_ENTRIES) cache.entries.clear();
  cache.entries.set(id as string | number, info);
  return info;
}
